import { Stack, Flex, Box, Grid, Text } from "@chakra-ui/react";
import { EnergyConsumptionReport } from "../../../api/types";
import backgroundImg from '../../../../../assets/report/background.png';
import { KILOWATT_HOUR, CARBON_FOOTPRINT_KG, MONETARY_VALUE, CARBON_INTENSITY } from "../../../../../utils/consts";
import { formatDate, getLocationsPercentage } from "../../../../../utils/helpers";
import CarbonIntensityIcon from "../../../../../assets/icons/CarbonIntensity";
import CloudIcon from "../../../../../assets/icons/CloudIcon";
import { ConsumedEnergyIcon } from "../../../../../assets/icons/ConsumedEnergy";
import CostForConsumptionIcon from "../../../../../assets/icons/CostConsumptionIcon";
import GeneratedIncomeIcon from "../../../../../assets/icons/GeneretedIncomeIcon";
import ProducedEnergyIcon from "../../../../../assets/icons/ProducedEnergyIcon";
import { CardPdf } from "./CardComponentPdf"
import logoSvg from '../../../../../assets/logo.svg'
import checkIcon from '../../../../../assets/checkIcon.png'

type Props = {
  startDate: Date;
  endDate: Date;
  data: EnergyConsumptionReport;
  displayData: any;
}

type ThumbnailProps = {
  locations: EnergyConsumptionReport
  startDate: Date
  endDate: Date
  data: any
}

export const Thumbnail = ({ data, displayData, startDate, endDate }: Props) => {
  return (
    <Stack
      width="100%"
      height="100%"
      alignItems="center"
      backgroundImage={`url('${backgroundImg}')`}
      backgroundRepeat="no-repeat"
      backgroundSize="cover"
      backgroundPosition="left"
    >
      <Flex direction="column" justifyContent="center" alignItems="center" width="100%" height="100%">
        <ThumbnailTitlePage
          locations={data}
          data={displayData}
          startDate={startDate}
          endDate={endDate}
        />
      </Flex>
    </Stack>
  );
}

export const ThumbnailTitlePage = ({
  locations,
  startDate,
  endDate,
  data,
}: ThumbnailProps) => {
  return (
    <Box
      height="100%"
      width="100%"
      display="flex"
      flexDirection="column"
      alignItems="center"
      textAlign="center"
      px={8}
      py={10}
      borderRadius="lg"
      overflow="hidden"
    >
      {/* Header */}
      <Stack spacing={4} mb={6}>
        <Text fontSize="40px" fontWeight="700" color="#2C3E50" mt="200px">
          {`${formatDate(startDate)} - ${formatDate(endDate)}`}
        </Text>
        <Text fontSize="30px" fontWeight="600" color="#2C3E50">
          Energy Consumption Report
        </Text>
      </Stack>

      {/* Locations */}
      <Flex
        wrap="wrap"
        gap={4}
        justifyContent="center"
        mb={10}
        maxWidth="100%"
      >
        {locations?.map((location, index) => (
          <Flex
            key={location.location_id}
            px={4}
            py={2}
            border="2px solid #157FEE"
            borderRadius="full"
            bg="#E8EEFD"
            alignItems="center"
            gap={2}
          >
            <img
              src={checkIcon}
              alt="check"
              style={{ width: "20px", height: "20px" }}
            />
            <Text
              data-location={`locationName${index}`}
              fontWeight="700"
              fontSize="16px"
              color="#157FEE"
              whiteSpace="nowrap"
            >
              {location.title}
            </Text>
          </Flex>
        ))}
      </Flex>

      {/* KPI Cards */}
      <Grid
        templateColumns="repeat(2, 1fr)"
        gap={6}
        width="100%"
        maxW="700px"
        py={6}
      >
        <CardPdf
          title="Total consumed energy"
          measurementType={KILOWATT_HOUR}
          value={data.currConsumption}
          percentageChange={getLocationsPercentage(
            data.currConsumption,
            data.prevConsumption
          )}
        >
          <ConsumedEnergyIcon boxSize="28px" />
        </CardPdf>
        <CardPdf
          title="Produced energy"
          measurementType={KILOWATT_HOUR}
          value={data.currGeneration}
          percentageChange={getLocationsPercentage(
            data.currGeneration,
            data.prevGeneration
          )}
        >
          <ProducedEnergyIcon boxSize="28px" />
        </CardPdf>
        <CardPdf
          title="Carbon footprint"
          measurementType={CARBON_FOOTPRINT_KG}
          value={data.currFootprint}
          percentageChange={getLocationsPercentage(
            data.currFootprint,
            data.prevFootprint
          )}
        >
          <CloudIcon boxSize="28px" />
        </CardPdf>
        <CardPdf
          title="Cost for consumption"
          measurementType={MONETARY_VALUE}
          value={data.currCost}
          percentageChange={getLocationsPercentage(
            data.currCost,
            data.prevCost
          )}
        >
          <CostForConsumptionIcon boxSize="28px" />
        </CardPdf>
        <CardPdf
          title="Generated income"
          measurementType={MONETARY_VALUE}
          value={data.currIncome}
          percentageChange={getLocationsPercentage(
            data.currIncome,
            data.prevIncome
          )}
        >
          <GeneratedIncomeIcon boxSize="28px" />
        </CardPdf>
        <CardPdf
          title="Carbon intensity"
          measurementType={CARBON_INTENSITY}
          value={data.intensity}
        >
          <CarbonIntensityIcon boxSize="28px" />
        </CardPdf>
      </Grid>

      {/* Logo */}
      <Box mt="auto" mb="80px">
        <img src={logoSvg} alt="logo" style={{ width: "150px", height: "20px" }} />
      </Box>
    </Box>
  );
};
